// src/components/playbook/DataProductPlaybookLayout.tsx
// Reusable Data Product Playbook layout component
// Can be embedded in PlaybookPage or DataStrategyOverview
// ============================================================
// CLIENT ID: Accepts clientId as required STRING prop
// Note: Line ~175 has intentional client-specific branding for '300' (North Star)
// This is display logic, not a hardcoded default - consider refactoring to 
// client profile lookup if more clients need custom branding
// ============================================================
import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

// Define the 13 standard playbook sections
const PLAYBOOK_SECTIONS = [
  { id: 'executive-summary', title: 'Executive Summary', number: 1 },
  { id: 'data-monetization', title: 'Data Monetization Overview', number: 2 },
  { id: 'strategic-assessment', title: 'Strategic Assessment Framework', number: 3 },
  { id: 'use-case-prioritization', title: 'Prioritization of Use Cases', number: 4 },
  { id: 'financial-modeling', title: 'Financial Modeling & ROI Assessment', number: 5 },
  { id: 'data-readiness', title: 'Data Utilization & Readiness', number: 6 },
  { id: 'implementation-roadmap', title: 'Technology Implementation Roadmap', number: 7 },
  { id: 'regulatory-compliance', title: 'Regulatory & Compliance', number: 8 },
  { id: 'go-to-market', title: 'Go-to-Market Strategy', number: 9 },
  { id: 'risk-mitigation', title: 'Risk Mitigation Plan', number: 10 },
  { id: 'actionable-outputs', title: 'Actionable Playbook Outputs', number: 11 },
  { id: 'tools-resources', title: 'Tools & Resources', number: 12 },
  { id: 'appendices', title: 'Appendices', number: 13 }
];

interface DataProductPlaybookLayoutProps {
  clientId: string;  // Required - parent MUST pass from AuthContext
  showTitle?: boolean;
}

// Table of Contents Component
const TableOfContents = ({ sections, activeSection, onSectionClick }) => {
  return (
    <div style={{
      position: 'sticky',
      top: '24px',
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      maxHeight: 'calc(100vh - 100px)',
      overflowY: 'auto'
    }}>
      <h2 style={{
        fontSize: '18px',
        fontWeight: '700',
        color: '#18365E',
        marginBottom: '20px',
        fontFamily: 'Montserrat, sans-serif'
      }}>
        Table of Contents
      </h2>
      
      <nav>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {sections.map((section) => (
            <li key={section.id} style={{ marginBottom: '8px' }}>
              <button
                onClick={() => onSectionClick(section.id)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  backgroundColor: activeSection === section.id ? '#f0f9ff' : 'transparent',
                  border: activeSection === section.id ? '1px solid #0ea5e9' : '1px solid transparent',
                  borderRadius: '6px',
                  color: activeSection === section.id ? '#0369a1' : '#374151',
                  fontSize: '14px',
                  fontWeight: activeSection === section.id ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (activeSection !== section.id) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== section.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  minWidth: '20px'
                }}>
                  {section.number}.
                </span>
                {section.title}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

// Section Renderer Component
const SectionRenderer = ({ section, content, sectionRefs }) => {
  return (
    <section 
      id={section.id}
      ref={el => sectionRefs.current[section.id] = el}
      style={{
        marginBottom: '48px',
        paddingBottom: '32px',
        borderBottom: '1px solid #e5e7eb'
      }}
    >
      <h2 style={{
        fontSize: '24px',
        fontWeight: '700',
        color: '#18365E',
        marginBottom: '24px',
        fontFamily: 'Montserrat, sans-serif',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <span style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#6b7280'
        }}>
          {section.number}.
        </span>
        {section.title}
      </h2>
      
      <div style={{
        fontSize: '16px',
        lineHeight: '1.7',
        color: '#374151',
        fontFamily: 'Montserrat, sans-serif'
      }}>
        {content ? (
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {content}
          </div>
        ) : (
          <div style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #fed7aa',
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center',
            color: '#92400e'
          }}>
            <div style={{ fontSize: '18px', marginBottom: '8px' }}>📝</div>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>This section is in progress</div>
            <div style={{ fontSize: '14px' }}>Content will be available once finalized</div>
          </div>
        )}
      </div>
    </section>
  );
};

// Helper function to get client display name
// TODO: Refactor to use client profile data from API/context instead of hardcoded check
const getClientDisplayName = (clientId: string): string => {
  // Known client display names - consider moving to client profile lookup
  const clientNames: Record<string, string> = {
    '300': 'North Star Consortium'
  };
  return clientNames[clientId] || 'your organization';
};

// Main Layout Component
export const DataProductPlaybookLayout: React.FC<DataProductPlaybookLayoutProps> = ({ 
  clientId,
  showTitle = true 
}) => {
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState('executive-summary');
  const sectionRefs = useRef({});
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Load playbook data
  useEffect(() => {
    const loadPlaybookData = async () => {
      // Guard: clientId is required
      if (!clientId) {
        console.warn('DataProductPlaybookLayout: clientId is required');
        setError('Client ID is required to load playbook');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Get client display name for personalization
        const clientDisplayName = getClientDisplayName(clientId);
        
        // Mock data for demonstration
        const mockSectionsData = {
          'executive-summary': {
            title: 'Executive Summary',
            content: `This Data Product Playbook provides a comprehensive strategic framework for ${clientDisplayName} to leverage data assets for monetization, operational improvement, and enhanced patient care.

Key strategic recommendations include implementing robust data governance frameworks, developing predictive analytics capabilities for population health management, and creating subscription-based data products for regional healthcare providers.

Through systematic assessment and prioritization, this playbook identifies high-value opportunities that align with organizational capabilities and market demand while maintaining the highest standards of privacy, security, and regulatory compliance.`,
            lastUpdated: new Date()
          },
          'data-monetization': {
            title: 'Data Monetization Overview',
            content: `Healthcare data monetization represents a significant opportunity to generate new revenue streams while improving patient outcomes and operational efficiency.

**Core Monetization Strategies:**
• Direct data product sales to pharmaceutical and research partners
• Subscription-based analytics services for regional providers
• Predictive modeling services for payer organizations
• Population health insights for public health agencies
• De-identified data for life sciences research

**Market Opportunity:**
The healthcare data analytics market is projected to grow at 23% CAGR through 2028, with strong demand for real-world evidence, population health insights, and AI/ML-ready datasets.

**Strategic Considerations:**
Organizations must balance revenue generation with patient trust, regulatory compliance, and ethical data use. Successful monetization requires robust governance, technical infrastructure, and clear value propositions for data consumers.`,
            lastUpdated: new Date()
          },
          'strategic-assessment': {
            title: 'Strategic Assessment Framework',
            content: `The strategic assessment framework provides a systematic approach to evaluating data monetization opportunities based on organizational readiness, market demand, and regulatory considerations.

**Assessment Dimensions:**
1. Data Quality & Completeness
2. Technical Infrastructure Capabilities
3. Regulatory Compliance Status
4. Market Demand Validation
5. Competitive Landscape Analysis
6. Organizational Readiness
7. Financial Viability

**Scoring Methodology:**
Each opportunity is evaluated across value potential and implementation feasibility, creating a prioritized portfolio of initiatives that balance quick wins with transformational programs.

**Risk Factors:**
Privacy concerns, regulatory changes, market competition, technical complexity, and organizational change management all factor into the assessment process.`,
            lastUpdated: new Date()
          }
        };

        // Try to load from Firestore/BigQuery backend, but fall back to mock data
        let sectionsData = {};
        try {
          // Check if Firebase is configured
          if (db) {
            const sectionsQuery = query(
              collection(db, 'client_playbook_sections'),
              where('client_id', '==', clientId)
            );
            const sectionsSnap = await getDocs(sectionsQuery);
            
            sectionsSnap.forEach((doc) => {
              const data = doc.data();
              sectionsData[data.section_id] = {
                title: data.title,
                content: data.customized_content || data.content,
                lastUpdated: data.lastUpdated?.toDate()
              };
            });
          }
          
          // If no data found, use mock data
          if (Object.keys(sectionsData).length === 0) {
            sectionsData = mockSectionsData;
          }
        } catch (firestoreError) {
          console.log('Using mock playbook data:', firestoreError.message);
          sectionsData = mockSectionsData;
        }

        setSections(sectionsData);
        setError(null);
      } catch (error) {
        console.error('Error loading playbook data:', error);
        setError('Failed to load playbook content');
        // Provide minimal fallback data
        setSections({
          'executive-summary': {
            title: 'Executive Summary',
            content: 'Playbook content is being generated. Please check back soon.',
            lastUpdated: new Date()
          }
        });
      } finally {
        setLoading(false);
      }
    };

    loadPlaybookData();
  }, [clientId]);

  // Intersection Observer for active section tracking
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, observerOptions);

    // Observe all section elements
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observerRef.current?.observe(ref);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [sections]);

  // Handle section click navigation
  const handleSectionClick = (sectionId: string) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '400px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px'
      }}>
        <div style={{
          fontSize: '16px',
          color: '#6b7280',
          fontFamily: 'Montserrat, sans-serif'
        }}>
          Loading playbook content...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '400px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px'
      }}>
        <div style={{
          fontSize: '16px',
          color: '#ef4444',
          fontFamily: 'Montserrat, sans-serif',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚠️</div>
          <div>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '300px 1fr',
      gap: '32px',
      padding: '24px'
    }}>
      {/* Table of Contents Sidebar */}
      <div>
        <TableOfContents 
          sections={PLAYBOOK_SECTIONS}
          activeSection={activeSection}
          onSectionClick={handleSectionClick}
        />
      </div>

      {/* Content Area */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        maxWidth: '960px'
      }}>
        {showTitle && (
          <div style={{ marginBottom: '40px' }}>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '700',
              color: '#18365E',
              marginBottom: '12px',
              fontFamily: 'Montserrat, sans-serif'
            }}>
              Data Product Playbook
            </h1>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              lineHeight: '1.6'
            }}>
              Strategic framework for data monetization and analytics excellence
            </p>
          </div>
        )}

        {PLAYBOOK_SECTIONS.map((section) => {
          const sectionContent = sections[section.id];
          return (
            <SectionRenderer
              key={section.id}
              section={section}
              content={sectionContent?.content}
              sectionRefs={sectionRefs}
            />
          );
        })}
      </div>

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .playbook-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DataProductPlaybookLayout;